import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { billingAccount } from '@/lib/queries/billing-accounts'
import { organization } from '@/lib/queries/organizations'
import { profile } from '@/lib/queries/profiles'
import { refineDisplayName } from '@/lib/validation/display-name'
import { useCurrentProfile } from '@/hooks/use-current-profile'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

function orgSettingsSchema(allowBrand: boolean) {
  return z.object({
    name: z
      .string()
      .min(1, 'Organization name is required')
      .max(100, 'Organization name must not exceed 100 characters')
      .superRefine(refineDisplayName({ allowBrand })),
  })
}

type OrgSettingsFormValues = z.infer<ReturnType<typeof orgSettingsSchema>>

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/settings/general'
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { orgId } = useParams({
    from: '/_authenticated/org/$orgId/settings/general',
  })

  return (
    <div className='max-w-2xl'>
      <OrganizationSettingsForm orgId={orgId} />
    </div>
  )
}

function OrganizationSettingsForm({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()
  const orgQuery = organization.findOne(orgId)
  const { data: org } = useSuspenseQuery(orgQuery)
  const { data: orgBillingAccount } = useSuspenseQuery(
    billingAccount.findOne(org.billing_account_id)
  )
  const { userId } = useCurrentProfile()
  const { data: viewer } = useSuspenseQuery(profile.findOne(userId))

  const form = useForm<OrgSettingsFormValues>({
    resolver: zodResolver(orgSettingsSchema(Boolean(viewer.is_super_admin))),
    values: { name: org.name ?? '' },
    resetOptions: { keepDirtyValues: true },
  })

  const { mutateAsync: saveOrganization, isPending: isSaving } = useMutation({
    mutationFn: async (values: OrgSettingsFormValues) => {
      const trimmedName = values.name.trim()
      return organization.update(orgId, { name: trimmedName })
    },
    onSuccess: async (updatedOrg) => {
      queryClient.setQueryData(orgQuery.queryKey, updatedOrg)
      await queryClient.invalidateQueries({
        queryKey: ['organizations', 'user'],
      })
      toast.success('Organization settings saved')
    },
    onError: () => {
      toast.error(
        'Failed to update organization settings. Please try again in a moment.'
      )
    },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    await saveOrganization(values)
  })

  const isSubmitDisabled = isSaving || !form.formState.isDirty

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='space-y-8'>
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization name</FormLabel>
              <FormControl className='w-[25rem]'>
                <Input
                  data-testid='org-name'
                  placeholder='Acme Inc.'
                  disabled={isSaving}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                This name is shown in the org switcher, invitations, and billing
                receipts.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>Billing account</FormLabel>
          <FormControl className='w-[25rem]'>
            <Input
              value={orgBillingAccount.name}
              disabled
              readOnly
              placeholder='Billing account'
            />
          </FormControl>
          <FormDescription>
            Managed centrally — plan and credits are on the Billing page.
            Contact support to change the billing account owner.
          </FormDescription>
        </FormItem>
        <Button
          type='submit'
          withLoading
          loading={isSaving}
          disabled={isSubmitDisabled}
          data-testid='save-org-settings'
        >
          Save settings
        </Button>
      </form>
    </Form>
  )
}
